import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { Button, Card } from '../../ui';
import './MarketingContent.css';

/**
 * MarketingContent - Marketing sections highlighting app benefits and competitive advantages
 * 
 * Features:
 * - Unique Bulgarian market focus
 * - Competitive advantage explanations
 * - Feature comparison sections
 * - Benefit-focused content blocks
 * - Trust signals and credibility indicators
 * - Conversion-optimized section layouts
 */
const MarketingContent = ({ onRegisterClick, className = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRegister = () => {
    if (onRegisterClick) {
      onRegisterClick();
    } else {
      navigate('/register');
    }
  };

  return (
    <div className={`marketing-content ${className}`}>
      {/* Bulgarian Market Focus Section */}
      <section className="marketing-section marketing-section--bulgarian-focus">
        <div className="marketing-section__container">
          <div className="marketing-section__content">
            <div className="marketing-section__text">
              <h2 className="marketing-section__title">
                {t('marketing.bulgarianFocus.title')}
              </h2>
              <p className="marketing-section__description">
                {t('marketing.bulgarianFocus.description')}
              </p>
              <ul className="marketing-section__benefits">
                <li className="marketing-section__benefit">
                  <span className="marketing-section__benefit-icon"></span>
                  <span>{t('marketing.bulgarianFocus.benefit1')}</span>
                </li>
                <li className="marketing-section__benefit">
                  <span className="marketing-section__benefit-icon"></span>
                  <span>{t('marketing.bulgarianFocus.benefit2')}</span>
                </li>
                <li className="marketing-section__benefit">
                  <span className="marketing-section__benefit-icon"></span>
                  <span>{t('marketing.bulgarianFocus.benefit3')}</span>
                </li>
                <li className="marketing-section__benefit">
                  <span className="marketing-section__benefit-icon"></span>
                  <span>{t('marketing.bulgarianFocus.benefit4')}</span>
                </li>
              </ul>
            </div>
            <div className="marketing-section__visual">
              <div className="marketing-visual marketing-visual--bulgaria">
                <div className="marketing-visual__icon">🇧🇬</div>
                <div className="marketing-visual__text">
                  {t('marketing.bulgarianFocus.visualText')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages Section */}
      <section className="marketing-section marketing-section--advantages">
        <div className="marketing-section__container">
          <div className="marketing-section__header">
            <h2 className="marketing-section__title">
              {t('marketing.advantages.title')}
            </h2>
            <p className="marketing-section__subtitle">
              {t('marketing.advantages.subtitle')}
            </p>
          </div>
          <div className="marketing-advantages">
            <div className="marketing-advantage">
              <div className="marketing-advantage__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="marketing-advantage__title">
                {t('marketing.advantages.advantage1.title')}
              </h3>
              <p className="marketing-advantage__description">
                {t('marketing.advantages.advantage1.description')}
              </p>
            </div>
            <div className="marketing-advantage">
              <div className="marketing-advantage__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="marketing-advantage__title">
                {t('marketing.advantages.advantage2.title')}
              </h3>
              <p className="marketing-advantage__description">
                {t('marketing.advantages.advantage2.description')}
              </p>
            </div>
            <div className="marketing-advantage">
              <div className="marketing-advantage__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="marketing-advantage__title">
                {t('marketing.advantages.advantage3.title')}
              </h3>
              <p className="marketing-advantage__description">
                {t('marketing.advantages.advantage3.description')}
              </p>
            </div>
            <div className="marketing-advantage">
              <div className="marketing-advantage__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="marketing-advantage__title">
                {t('marketing.advantages.advantage4.title')}
              </h3>
              <p className="marketing-advantage__description">
                {t('marketing.advantages.advantage4.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="marketing-section marketing-section--benefits">
        <div className="marketing-section__container">
          <div className="marketing-section__header">
            <h2 className="marketing-section__title">
              {t('marketing.benefits.title')}
            </h2>
            <p className="marketing-section__subtitle">
              {t('marketing.benefits.subtitle')}
            </p>
          </div>
          <div className="marketing-benefits">
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">💰</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit1.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit1.description')}
              </p>
            </Card>
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">⚡</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit2.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit2.description')}
              </p>
            </Card>
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">🎯</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit3.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit3.description')}
              </p>
            </Card>
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">🔒</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit4.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit4.description')}
              </p>
            </Card>
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">📊</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit5.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit5.description')}
              </p>
            </Card>
            <Card className="marketing-benefit-card">
              <div className="marketing-benefit-card__icon">🚀</div>
              <h3 className="marketing-benefit-card__title">
                {t('marketing.benefits.benefit6.title')}
              </h3>
              <p className="marketing-benefit-card__description">
                {t('marketing.benefits.benefit6.description')}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="marketing-section marketing-section--trust">
        <div className="marketing-section__container">
          <div className="marketing-section__header">
            <h2 className="marketing-section__title">
              {t('marketing.trust.title')}
            </h2>
            <p className="marketing-section__subtitle">
              {t('marketing.trust.subtitle')}
            </p>
          </div>
          <div className="marketing-trust">
            <div className="marketing-trust__item">
              <div className="marketing-trust__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="marketing-trust__content">
                <h3 className="marketing-trust__title">
                  {t('marketing.trust.trust1.title')}
                </h3>
                <p className="marketing-trust__description">
                  {t('marketing.trust.trust1.description')}
                </p>
              </div>
            </div>
            <div className="marketing-trust__item">
              <div className="marketing-trust__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="marketing-trust__content">
                <h3 className="marketing-trust__title">
                  {t('marketing.trust.trust2.title')}
                </h3>
                <p className="marketing-trust__description">
                  {t('marketing.trust.trust2.description')}
                </p>
              </div>
            </div>
            <div className="marketing-trust__item">
              <div className="marketing-trust__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="marketing-trust__content">
                <h3 className="marketing-trust__title">
                  {t('marketing.trust.trust3.title')}
                </h3>
                <p className="marketing-trust__description">
                  {t('marketing.trust.trust3.description')}
                </p>
              </div>
            </div>
            <div className="marketing-trust__item">
              <div className="marketing-trust__icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="marketing-trust__content">
                <h3 className="marketing-trust__title">
                  {t('marketing.trust.trust4.title')}
                </h3>
                <p className="marketing-trust__description">
                  {t('marketing.trust.trust4.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion CTA Section */}
      <section className="marketing-section marketing-section--cta">
        <div className="marketing-section__container">
          <div className="marketing-cta">
            <h2 className="marketing-cta__title">
              {t('marketing.cta.title')}
            </h2>
            <p className="marketing-cta__description">
              {t('marketing.cta.description')}
            </p>
            <div className="marketing-cta__actions">
              <Button
                variant="primary"
                size="large"
                onClick={handleRegister}
                className="marketing-cta__button"
              >
                {t('marketing.cta.button')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

MarketingContent.propTypes = {
  onRegisterClick: PropTypes.func,
  className: PropTypes.string
};

export default MarketingContent;

